/**
 * One-time style import from pre-SQLite on-disk layouts into an already-open, migrated CLI DB.
 * Runs after {@link migrateCliSqlite}; not part of `__migrations` (schema vs data separation).
 * Idempotent: missing dirs/files are skipped; safe to run on every open.
 */

import fs from 'node:fs';
import type { Database } from 'node-sqlite3-wasm';
import { INDEX_DIR } from '../../files/index/constants.js';
import type { LogFn } from '../../log.js';
import { getPromptQueuesDirectory } from '../../prompt-turn-queue/paths.js';
import { archiveLegacyFileIndexJsonFiles } from './archive-file-index-json.js';
import {
  importLegacyAgentSessionsFromDisk,
  LEGACY_AGENT_SESSION_DIR,
} from './import-agent-sessions-from-disk.js';
import { importLegacyPromptQueuesFromDisk } from './import-prompt-queues-from-disk.js';

/**
 * True when on-disk legacy JSON still exists that {@link importCliSqliteLegacyDiskData} will try to process.
 * Used to log migration start/finish only when that path actually runs.
 */
export function legacyCliDiskMigrationIsPending(): boolean {
  try {
    if (fs.existsSync(INDEX_DIR)) {
      for (const name of fs.readdirSync(INDEX_DIR)) {
        if (name.startsWith('.file-index-') && name.endsWith('.json')) return true;
      }
    }
  } catch {
    /* treat as no pending file-index legacy */
  }
  try {
    if (fs.existsSync(LEGACY_AGENT_SESSION_DIR)) {
      if (fs.readdirSync(LEGACY_AGENT_SESSION_DIR).some((n) => n.endsWith('.json'))) return true;
    }
  } catch {
    /* ignore */
  }
  try {
    const dir = getPromptQueuesDirectory();
    if (fs.existsSync(dir) && fs.readdirSync(dir).some((n) => n.endsWith('.json'))) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Import legacy JSON on disk into SQLite and archive originals under `~/.buildautomaton/old-version/`.
 * Call only after schema migrations have created the target tables.
 *
 * When `log` is set and legacy work is pending, logs one line before and one after the import steps.
 */
export function importCliSqliteLegacyDiskData(db: Database, log?: LogFn): void {
  const pending = legacyCliDiskMigrationIsPending();
  if (pending && log) {
    log('Migrating legacy on-disk CLI data to SQLite…');
  }
  archiveLegacyFileIndexJsonFiles();
  importLegacyAgentSessionsFromDisk(db);
  importLegacyPromptQueuesFromDisk(db);
  if (pending && log) {
    log('Legacy on-disk CLI data migration finished.');
  }
}
